import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { Helmet } from 'react-helmet';
import styled, { ThemeProvider } from 'styled-components';
import { Head } from '@components';
import WhatsAppFab from './whatsapp-fab';
import { IconLogo } from '@components/icons';
import { GlobalStyle, theme } from '@styles';
import { email, socialMedia } from '@config';
import { LanguageProvider, useLanguage } from '../context/language';

const accent = '#0B346A';
const ink = '#1B2330';
const paper = '#F6F2E9';
const paperSoft = '#FBF8F1';
const line = '#E6E0D2';

const getHomePath = language => (language === 'fr' ? '/fr' : '/en');

const normalizeNavUrl = url => {
  if (url === '#jobs') {
    return '#experience';
  }

  if (url === '#services') {
    return '#expertise';
  }

  return url;
};

const getLanguagePath = language => `/${language}`;

const experienceTags = {
  INGroupe: [
    'Android',
    'iOS',
    'Flutter',
    'React Native',
    'KMP',
    'Ktor',
    'Docker',
    'Docker Compose',
    'SDKs',
    'Issuer',
    'Verifier',
    'Proximity',
  ],
  Stuart: ['Flutter', 'Node.js', 'BFF', 'Docker', 'Kafka', 'Fastlane'],
  Carrefour: ['Android', 'Kotlin', 'MVI', 'Clean Architecture', 'Hilt', 'Jenkins'],
  JCDecaux: ['Android', 'iOS', 'Kotlin', 'Swift', 'RxJava', 'RxSwift'],
  HSBC: ['iOS', 'Swift', 'MVVM', 'Security', 'Wallet', 'Production'],
  Bulldozer: ['Android', 'iOS', 'Java', 'Kotlin', 'Swift', 'Dart', 'Fastlane'],
};

const getExperienceSummary = item => {
  if (item.summary) {
    return item.summary;
  }

  if (!item.points || item.points.length === 0) {
    return '';
  }

  return item.points.slice(0, 2).join(' ');
};

const buildPortfolioContent = content => {
  const hero = content.hero;
  const about = content.about;
  const jobs = content.jobs;
  const services = content.services;
  const cv = content.cv;
  const contact = content.contact;
  const isFrench = hero.intro.toLowerCase().startsWith('bonjour');

  return {
    nav: content.nav.links.map(link => ({ ...link, url: normalizeNavUrl(link.url) })),
    hero: {
      kicker: isFrench
        ? 'Bonjour, je suis Hedi Hidouri · Paris, France'
        : 'Hi, I am Hedi Hidouri · Paris, France',
      headTop: isFrench ? 'Ingénieur' : 'Senior Software',
      headAccent: isFrench ? 'Logiciel Senior.' : 'Engineer.',
      body: hero.description,
      cta: hero.cta,
      cvCta: cv.downloadCta,
      stats: [
        ...(hero.quickStats || []).map(stat => ({ label: stat.label, value: stat.value })),
        {
          label: isFrench ? 'Freelance / conseil' : 'Freelance / consulting',
          value: isFrench ? 'Disponible' : 'Available',
        },
      ],
    },
    about: {
      kicker: isFrench ? '01 - A propos' : '01 - About',
      lead: isFrench
        ? 'Une ingenierie de bout en bout, des interfaces mobiles aux services backend et SDK qui les font vivre.'
        : 'End-to-end engineering, from the mobile interface to the backend services and SDKs behind it.',
      body: [...about.paragraphs, ...about.valueList].join(' '),
      pillars: [
        {
          title: isFrench ? 'Ingenierie de bout en bout' : 'End-to-end engineering',
          description: isFrench
            ? 'Mobile, backend, architecture et CI/CD sur toute la chaine de livraison.'
            : 'Mobile, backend, architecture, and CI/CD across the full delivery path.',
        },
        {
          title: isFrench ? 'Mobile + backend' : 'Mobile + backend',
          description: isFrench
            ? 'Android, iOS, Flutter, KMP, Ktor, Node.js et APIs adaptees au produit.'
            : 'Android, iOS, Flutter, KMP, Ktor, Node.js, and product-focused APIs.',
        },
        {
          title: isFrench ? 'Architecture durable' : 'Durable architecture',
          description: isFrench
            ? 'Des fondations modulaires qui restent maintenables quand le produit grandit.'
            : 'Modular foundations that stay maintainable as the product grows.',
        },
        {
          title: isFrench ? 'Delivery pragmatique' : 'Pragmatic delivery',
          description: isFrench
            ? 'Des decisions orientees production, qualite et impact mesurable.'
            : 'Production-minded decisions focused on quality and measurable impact.',
        },
        {
          title: isFrench ? 'Qualite production' : 'Production quality',
          description: isFrench
            ? 'Tests, observabilite, crash analytics et discipline de release.'
            : 'Testing, observability, crash analytics, and release discipline.',
        },
        {
          title: isFrench ? 'Delivery assiste par IA' : 'AI-assisted delivery',
          description: isFrench
            ? 'Automatiser ce qui peut etre accelere sans perdre la maitrise technique.'
            : 'Automating what makes sense without losing technical ownership.',
        },
      ],
    },
    exp: {
      kicker: isFrench ? '02 - Experience' : '02 - Experience',
      title: isFrench ? 'Mes livraisons' : 'Delivery highlights',
      currentLabel: isFrench ? 'En poste' : 'Current',
      items: jobs.items.map((item, index) => ({
        ...item,
        current: index === 0,
        summary: getExperienceSummary(item),
        tags: experienceTags[item.company] || [],
      })),
    },
    services: {
      kicker: isFrench ? '03 - Expertise' : '03 - Expertise',
      title: services.heading,
      items: services.items.map((item, index) => ({
        ...item,
        number: String(index + 1).padStart(2, '0'),
      })),
    },
    skills: {
      kicker: isFrench ? '04 - Competences' : '04 - Skills',
      title: isFrench ? 'Mes outils' : 'Tools of the trade',
      groups: [
        {
          label: isFrench ? 'Plateformes' : 'Platforms',
          items: ['Android', 'iOS', 'Flutter', 'Kotlin Multiplatform'],
        },
        {
          label: isFrench ? 'Langages' : 'Languages',
          items: ['Kotlin', 'Swift', 'Dart', 'TypeScript', 'JavaScript', 'Python'],
        },
        {
          label: 'Backend',
          items: ['Ktor', 'Node.js', 'REST APIs', 'OpenAPI', 'BFF'],
        },
        {
          label: isFrench ? 'Delivery' : 'Delivery',
          items: ['Docker', 'Docker Compose', 'GitHub Actions', 'Jenkins', 'Fastlane'],
        },
        {
          label: 'Architecture',
          items: ['Clean Architecture', 'MVI', 'MVVM', 'MVP'],
        },
        {
          label: isFrench ? 'Qualite' : 'Quality',
          items: [
            'Firebase',
            'Crashlytics',
            'Analytics',
            isFrench ? 'Observabilite' : 'Observability',
          ],
        },
      ],
    },
    cv: {
      kicker: '05 - CV',
      title: cv.title,
      body: cv.description,
      cta: cv.downloadCta,
      file: cv.file,
    },
    contact: {
      kicker: isFrench ? '06 - Contact' : '06 - Contact',
      title: contact.title,
      body: contact.description,
      cta: contact.cta,
      availability: isFrench
        ? 'Disponible pour freelance ou conseil'
        : 'Available for freelance or consulting',
      emailLabel: isFrench ? 'Ecrivez-moi' : 'Email me',
      linkedinLabel: 'LinkedIn',
    },
    footer: {
      tagline: isFrench ? 'Ingénieur Logiciel Senior · Paris' : 'Senior Software Engineer · Paris',
      rights: '© 2026 Hedi Hidouri',
    },
  };
};

const HMark = ({ compact }) => (
  <span className={compact ? 'h-mark h-mark--compact' : 'h-mark'} aria-hidden="true">
    <IconLogo />
  </span>
);

HMark.propTypes = {
  compact: PropTypes.bool,
};

HMark.defaultProps = {
  compact: false,
};

const PortfolioRoot = styled.div`
  --portfolio-accent: ${accent};
  --portfolio-ink: ${ink};
  --portfolio-paper: ${paper};
  --portfolio-paper-soft: ${paperSoft};
  --portfolio-line: ${line};

  min-height: 100vh;
  overflow-x: hidden;
  background: var(--portfolio-paper);
  color: var(--portfolio-ink);
  font-family: 'Geist', var(--font-sans);

  .h-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    flex: 0 0 auto;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 18px 34px rgba(27, 35, 48, 0.14);

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .h-mark--compact {
    width: 34px;
    height: 34px;
    border-radius: 9px;
  }

  .site-header {
    position: sticky;
    top: 0;
    z-index: 50;
    background: rgba(246, 242, 233, 0.88);
    border-bottom: 1px solid var(--portfolio-line);
    backdrop-filter: saturate(140%) blur(12px);
  }

  .accent-bar {
    height: 3px;
    background: var(--portfolio-accent);
  }

  .nav-inner {
    max-width: 1180px;
    height: 73px;
    margin: 0 auto;
    padding: 0 clamp(20px, 4vw, 40px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }

  .brand,
  .footer-brand {
    display: flex;
    align-items: center;
    gap: 13px;
    color: var(--portfolio-ink);
  }

  .brand-name,
  .footer-name {
    font-family: 'Newsreader', serif;
    font-size: 19px;
    font-weight: 500;
  }

  .desktop-nav,
  .nav-actions,
  .language-inline,
  .mobile-actions,
  .hero-actions,
  .stats,
  .tags,
  .contact-actions,
  .footer-inner {
    display: flex;
    align-items: center;
  }

  .desktop-nav {
    gap: clamp(20px, 2.6vw, 36px);
    margin-left: auto;
  }

  .desktop-nav a {
    padding: 6px 0;
    border-bottom: 1.5px solid transparent;
    color: #3d4350;
    font-size: 14.5px;
  }

  .desktop-nav a:hover,
  .desktop-nav a:focus-visible {
    border-bottom-color: var(--portfolio-accent);
    color: var(--portfolio-accent);
  }

  .nav-actions {
    gap: 18px;
  }

  .language-inline {
    gap: 7px;
    color: #a39a8c;
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 12.5px;
    letter-spacing: 0.06em;
  }

  .language-inline a.active {
    color: var(--portfolio-ink);
    font-weight: 600;
  }

  .language-separator {
    color: #c8c1b1;
  }

  .dark-pill,
  .primary-pill,
  .secondary-pill {
    border-radius: 999px;
    font-size: 15px;
    font-weight: 500;
    line-height: 1;
  }

  .dark-pill {
    padding: 11px 21px;
    background: var(--portfolio-ink);
    color: var(--portfolio-paper);
  }

  .primary-pill {
    padding: 15px 30px;
    background: var(--portfolio-accent);
    color: #fff;
  }

  .secondary-pill {
    padding: 15px 30px;
    border: 1px solid #c9c1b0;
    background: transparent;
    color: var(--portfolio-ink);
  }

  .dark-pill:hover,
  .primary-pill:hover,
  .secondary-pill:hover,
  .dark-pill:focus-visible,
  .primary-pill:focus-visible,
  .secondary-pill:focus-visible {
    transform: translateY(-2px);
  }

  .dark-pill:hover,
  .dark-pill:focus-visible {
    background: var(--portfolio-accent);
    color: #fff;
  }

  .secondary-pill:hover,
  .secondary-pill:focus-visible {
    border-color: var(--portfolio-accent);
    color: var(--portfolio-accent);
  }

  .mobile-menu-button {
    display: none;
    width: 44px;
    height: 44px;
    border: 1px solid #d8d1c1;
    border-radius: 10px;
    background: transparent;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  .mobile-menu-button span {
    width: 20px;
    height: 2px;
    background: var(--portfolio-ink);
  }

  .mobile-panel {
    display: none;
  }

  main {
    width: 100%;
    max-width: none;
    min-height: 0;
    padding: 0;
  }

  section {
    max-width: none;
    padding: 0;
  }

  .hero {
    position: relative;
    overflow: hidden;
    padding: clamp(64px, 9vw, 116px) clamp(20px, 4vw, 40px) clamp(56px, 7vw, 92px);
  }

  .hero-glow {
    position: absolute;
    left: 50%;
    top: 8%;
    z-index: 0;
    width: min(820px, 92vw);
    height: 620px;
    transform: translateX(-50%);
    background: radial-gradient(ellipse at center, rgba(11, 52, 106, 0.16), transparent 66%);
    pointer-events: none;
  }

  .hero-watermark {
    position: absolute;
    right: clamp(-120px, -6vw, -40px);
    top: 96px;
    z-index: 0;
    width: clamp(360px, 46vw, 620px);
    opacity: 0.04;
    pointer-events: none;
  }

  .hero-inner {
    position: relative;
    z-index: 1;
    max-width: 940px;
    margin: 0 auto;
    text-align: center;
  }

  .kicker {
    margin-bottom: 16px;
    color: var(--portfolio-accent);
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
  }

  .hero .kicker {
    margin-bottom: clamp(20px, 3vw, 30px);
    font-size: clamp(11px, 1.4vw, 13px);
    letter-spacing: 0.22em;
  }

  .hero-title,
  .section-title,
  .experience-company,
  .card-title,
  .footer-name {
    font-family: 'Newsreader', serif;
  }

  .hero-title {
    margin: 0 0 clamp(22px, 3vw, 34px);
    color: var(--portfolio-ink);
    font-size: clamp(48px, 9vw, 116px);
    font-weight: 400;
    line-height: 0.95;
    letter-spacing: -0.02em;
  }

  .hero-title em {
    color: var(--portfolio-accent);
    font-style: italic;
  }

  .hero-body {
    max-width: 660px;
    margin: 0 auto clamp(30px, 4vw, 40px);
    color: #3d4350;
    font-size: clamp(16px, 1.9vw, 19px);
    line-height: 1.62;
  }

  .hero-actions {
    justify-content: center;
    flex-wrap: wrap;
    gap: 13px;
    margin-bottom: clamp(48px, 6vw, 64px);
  }

  .stats {
    max-width: 780px;
    margin: 0 auto;
    align-items: stretch;
    justify-content: center;
    flex-wrap: wrap;
    border-top: 1px solid var(--portfolio-line);
    border-bottom: 1px solid var(--portfolio-line);
  }

  .stat {
    flex: 1 1 200px;
    padding: 22px 16px;
    border-left: 1px solid var(--portfolio-line);
  }

  .stat:first-child {
    border-left: 0;
  }

  .stat-value {
    color: var(--portfolio-ink);
    font-family: 'Newsreader', serif;
    font-size: clamp(22px, 2.6vw, 27px);
  }

  .stat-label {
    margin-top: 5px;
    color: #837a6e;
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .section-block {
    scroll-margin-top: 96px;
    padding: clamp(72px, 9vw, 128px) clamp(20px, 4vw, 40px);
  }

  .section-block.soft {
    border-top: 1px solid #ece6d9;
    background: var(--portfolio-paper-soft);
  }

  .section-inner {
    max-width: 1120px;
    margin: 0 auto;
  }

  .section-heading {
    margin-bottom: clamp(40px, 5vw, 64px);
    text-align: center;
  }

  .section-title {
    max-width: 880px;
    margin: 0 auto;
    color: var(--portfolio-ink);
    font-size: clamp(30px, 5vw, 52px);
    font-weight: 400;
    line-height: 1.08;
    letter-spacing: -0.015em;
  }

  .about-body {
    max-width: 760px;
    margin: 0 auto clamp(48px, 6vw, 64px);
    color: #3d4350;
    font-size: clamp(16px, 1.8vw, 18px);
    line-height: 1.7;
    text-align: center;
  }

  .pillar-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1px;
    overflow: hidden;
    border: 1px solid var(--portfolio-line);
    border-radius: 12px;
    background: var(--portfolio-line);
  }

  .pillar-card {
    padding: 26px 24px;
    background: #fcfaf4;
  }

  .pillar-card:hover {
    background: #f4f1ea;
  }

  .marker {
    width: 9px;
    height: 9px;
    margin-bottom: 16px;
    border-radius: 2px;
    background: var(--portfolio-accent);
  }

  .pillar-title {
    margin-bottom: 7px;
    color: var(--portfolio-ink);
    font-family: 'Newsreader', serif;
    font-size: 20px;
  }

  .pillar-text {
    color: #6f6a60;
    font-size: 14px;
    line-height: 1.55;
  }

  .experience-list {
    border-top: 1px solid var(--portfolio-line);
  }

  .experience-row {
    display: grid;
    grid-template-columns: minmax(200px, 240px) 1fr;
    gap: clamp(12px, 3vw, 44px);
    padding: clamp(26px, 3.4vw, 38px) 4px;
    border-bottom: 1px solid var(--portfolio-line);
  }

  .experience-row:hover {
    background: var(--portfolio-paper-soft);
  }

  .experience-heading-line {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .experience-company {
    color: var(--portfolio-ink);
    font-size: clamp(23px, 2.8vw, 30px);
  }

  .current-badge {
    padding: 4px 9px;
    border-radius: 999px;
    background: rgba(11, 52, 106, 0.12);
    color: var(--portfolio-accent);
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 10.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .experience-role {
    margin-top: 6px;
    color: #837a6e;
    font-size: 14.5px;
  }

  .experience-summary {
    margin: 0 0 16px;
    color: #3d4350;
    font-size: clamp(15px, 1.7vw, 16.5px);
    line-height: 1.62;
  }

  .tags {
    flex-wrap: wrap;
    gap: 8px;
  }

  .tag {
    border: 1px solid var(--portfolio-line);
    border-radius: 6px;
    background: #fcfaf4;
    color: #5a5750;
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 11.5px;
    padding: 5px 11px;
  }

  .dark-section {
    background: var(--portfolio-ink);
    color: var(--portfolio-paper);
  }

  .dark-section .kicker {
    color: #8fa8d0;
  }

  .dark-section .section-title {
    color: var(--portfolio-paper);
  }

  .service-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
    gap: 16px;
  }

  .service-card {
    padding: 28px 26px;
    border: 1px solid #2f3a4c;
    border-radius: 14px;
    background: #222c3c;
  }

  .service-card:hover {
    border-color: var(--portfolio-accent);
    background: #26303f;
    transform: translateY(-4px);
  }

  .service-number {
    margin-bottom: 18px;
    color: #8fa8d0;
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 12px;
  }

  .service-title {
    margin-bottom: 10px;
    color: var(--portfolio-paper);
    font-family: 'Newsreader', serif;
    font-size: 22px;
    line-height: 1.2;
  }

  .service-text {
    color: #aeb6c2;
    font-size: 14.5px;
    line-height: 1.6;
  }

  .skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 28px 40px;
  }

  .skill-label {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--portfolio-line);
    color: #837a6e;
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .skill-list {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
  }

  .skill-pill {
    border: 1px solid #e2dbcc;
    border-radius: 999px;
    background: #fcfaf4;
    color: #2a3140;
    font-size: 14px;
    padding: 8px 15px;
  }

  .skill-pill:hover {
    border-color: var(--portfolio-accent);
    color: var(--portfolio-accent);
  }

  .cv-section {
    padding: clamp(64px, 8vw, 104px) clamp(20px, 4vw, 40px);
  }

  .cv-inner {
    max-width: 720px;
    margin: 0 auto;
    text-align: center;
  }

  .cv-body,
  .contact-body {
    font-size: 17px;
    line-height: 1.6;
  }

  .cv-body {
    margin: 0 0 30px;
    color: #3d4350;
  }

  .contact-section {
    position: relative;
    overflow: hidden;
  }

  .contact-watermark {
    position: absolute;
    left: -60px;
    bottom: -80px;
    width: 360px;
    opacity: 0.05;
    pointer-events: none;
  }

  .contact-inner {
    position: relative;
    z-index: 1;
    max-width: 880px;
    margin: 0 auto;
    text-align: center;
  }

  .contact-title {
    margin: 0 0 20px;
    color: var(--portfolio-paper);
    font-family: 'Newsreader', serif;
    font-size: clamp(34px, 5.6vw, 60px);
    font-weight: 400;
    line-height: 1.02;
    letter-spacing: -0.02em;
  }

  .contact-body {
    max-width: 560px;
    margin: 0 auto 36px;
    color: #aeb6c2;
  }

  .availability {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 30px;
    color: #8fa8d0;
    font-family: 'Geist Mono', var(--font-mono);
    font-size: 12.5px;
    letter-spacing: 0.06em;
  }

  .availability-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #8fa8d0;
    box-shadow: 0 0 0 4px rgba(143, 168, 208, 0.18);
  }

  .contact-actions {
    justify-content: center;
    flex-wrap: wrap;
    gap: 13px;
  }

  .light-pill,
  .outline-pill {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    border-radius: 999px;
    font-size: 15px;
    font-weight: 500;
    padding: 15px 30px;
  }

  .light-pill {
    background: var(--portfolio-paper);
    color: var(--portfolio-accent);
  }

  .outline-pill {
    border: 1px solid #3a4456;
    color: var(--portfolio-paper);
  }

  .outline-pill:hover,
  .outline-pill:focus-visible {
    border-color: var(--portfolio-accent);
    background: #222c3c;
    color: var(--portfolio-paper);
  }

  .light-pill svg,
  .outline-pill svg,
  .cv-icon {
    width: 17px;
    height: 17px;
  }

  .site-footer {
    background: #161d29;
    color: #aeb6c2;
    padding: clamp(40px, 5vw, 56px) clamp(20px, 4vw, 40px);
  }

  .footer-inner {
    max-width: 1120px;
    margin: 0 auto;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 24px;
  }

  .footer-name {
    color: var(--portfolio-paper);
    font-size: 17px;
  }

  .footer-tagline,
  .footer-rights {
    color: #6d7689;
    font-size: 12.5px;
  }

  .footer-rights {
    font-family: 'Geist Mono', var(--font-mono);
    letter-spacing: 0.04em;
  }

  [data-reveal] {
    animation: revealUp 0.7s ease both;
  }

  .section-block [data-reveal] {
    animation-delay: 0.05s;
  }

  @keyframes revealUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      scroll-behavior: auto !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media (max-width: 860px) {
    .desktop-nav,
    .nav-actions {
      display: none;
    }

    .mobile-menu-button {
      display: flex;
    }

    .mobile-panel {
      display: block;
      border-top: 1px solid var(--portfolio-line);
      background: var(--portfolio-paper);
      padding: 14px clamp(20px, 4vw, 40px) 24px;
    }

    .mobile-panel nav {
      display: flex;
      flex-direction: column;
    }

    .mobile-panel a:not(.mobile-cta) {
      border-bottom: 1px solid var(--portfolio-line);
      color: var(--portfolio-ink);
      font-family: 'Newsreader', serif;
      font-size: 22px;
      padding: 13px 0;
    }

    .mobile-actions {
      justify-content: space-between;
      margin-top: 20px;
    }

    .mobile-cta {
      background: var(--portfolio-accent);
      color: #fff;
      font-size: 14.5px;
      font-weight: 500;
      padding: 12px 22px;
      border-radius: 999px;
    }

    .experience-row {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 560px) {
    .brand-name {
      display: none;
    }

    .hero {
      padding-top: 52px;
    }

    .stat {
      border-left: 0;
      border-top: 1px solid var(--portfolio-line);
    }

    .stat:first-child {
      border-top: 0;
    }

    .primary-pill,
    .secondary-pill,
    .dark-pill {
      width: 100%;
      justify-content: center;
      text-align: center;
    }

    .hero-title {
      font-size: clamp(44px, 17vw, 72px);
    }
  }
`;

const PortfolioPageContent = ({ location }) => {
  const { content, language } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const portfolio = buildPortfolioContent(content);
  const linkedInUrl = socialMedia.find(item => item.name.toLowerCase() === 'linkedin')?.url;
  const homePath = getHomePath(language);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <Head pathname={location.pathname} />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500;600&family=Newsreader:ital,wght@0,300..700;1,300..700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <PortfolioRoot>
        <header className="site-header">
          <div className="accent-bar" />
          <div className="nav-inner">
            <a className="brand" href={`${homePath}#top`} aria-label={content.nav.homeAriaLabel}>
              <HMark />
              <span className="brand-name">Hedi Hidouri</span>
            </a>

            <nav className="desktop-nav" aria-label="Primary navigation">
              {portfolio.nav.map(item => (
                <a key={item.url} href={item.url}>
                  {item.name}
                </a>
              ))}
            </nav>

            <div className="nav-actions">
              <div className="language-inline" aria-label={content.nav.languageSwitcherAriaLabel}>
                <Link className={language === 'en' ? 'active' : ''} to={getLanguagePath('en')}>
                  EN
                </Link>
                <span className="language-separator">/</span>
                <Link className={language === 'fr' ? 'active' : ''} to={getLanguagePath('fr')}>
                  FR
                </Link>
              </div>
              <a className="dark-pill" href="#contact">
                {portfolio.hero.cta}
              </a>
            </div>

            <button
              className="mobile-menu-button"
              type="button"
              aria-label={content.nav.menuButtonAriaLabel}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(current => !current)}>
              <span />
              <span />
              <span />
            </button>
          </div>

          {menuOpen && (
            <div className="mobile-panel">
              <nav aria-label="Mobile navigation">
                {portfolio.nav.map(item => (
                  <a key={item.url} href={item.url} onClick={closeMenu}>
                    {item.name}
                  </a>
                ))}
              </nav>
              <div className="mobile-actions">
                <div className="language-inline" aria-label={content.nav.languageSwitcherAriaLabel}>
                  <Link className={language === 'en' ? 'active' : ''} to={getLanguagePath('en')}>
                    EN
                  </Link>
                  <span className="language-separator">/</span>
                  <Link className={language === 'fr' ? 'active' : ''} to={getLanguagePath('fr')}>
                    FR
                  </Link>
                </div>
                <a className="mobile-cta" href="#contact" onClick={closeMenu}>
                  {portfolio.hero.cta}
                </a>
              </div>
            </div>
          )}
        </header>

        <main id="top">
          <section className="hero" aria-labelledby="hero-title">
            <div className="hero-glow" />
            <div className="hero-watermark">
              <IconLogo />
            </div>
            <div className="hero-inner">
              <p className="kicker">{portfolio.hero.kicker}</p>
              <h1 className="hero-title" id="hero-title">
                {portfolio.hero.headTop}
                <br />
                <em>{portfolio.hero.headAccent}</em>
              </h1>
              <p className="hero-body">{portfolio.hero.body}</p>
              <div className="hero-actions">
                <a className="primary-pill" href="#contact">
                  {portfolio.hero.cta}
                </a>
                <a
                  className="secondary-pill"
                  href={portfolio.cv.file.url}
                  download={portfolio.cv.file.downloadName}>
                  {portfolio.hero.cvCta}
                </a>
              </div>
              <div className="stats" aria-label="Profile summary">
                {portfolio.hero.stats.map(stat => (
                  <div className="stat" key={`${stat.label}-${stat.value}`}>
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="about" className="section-block soft" aria-labelledby="about-title">
            <div className="section-inner" data-reveal="">
              <div className="section-heading">
                <p className="kicker">{portfolio.about.kicker}</p>
                <h2 className="section-title" id="about-title">
                  {portfolio.about.lead}
                </h2>
              </div>
              <p className="about-body">{portfolio.about.body}</p>
              <div className="pillar-grid">
                {portfolio.about.pillars.map(item => (
                  <article className="pillar-card" key={item.title}>
                    <div className="marker" />
                    <h3 className="pillar-title">{item.title}</h3>
                    <p className="pillar-text">{item.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="experience" className="section-block" aria-labelledby="experience-title">
            <span id="jobs" aria-hidden="true" />
            <div className="section-inner" data-reveal="">
              <div className="section-heading">
                <p className="kicker">{portfolio.exp.kicker}</p>
                <h2 className="section-title" id="experience-title">
                  {portfolio.exp.title}
                </h2>
              </div>
              <div className="experience-list">
                {portfolio.exp.items.map(item => (
                  <article className="experience-row" key={`${item.company}-${item.range}`}>
                    <div>
                      <div className="experience-heading-line">
                        <h3 className="experience-company">{item.company}</h3>
                        {item.current && (
                          <span className="current-badge">{portfolio.exp.currentLabel}</span>
                        )}
                      </div>
                      <div className="experience-role">
                        {item.title} · {item.range}
                      </div>
                    </div>
                    <div>
                      <p className="experience-summary">{item.summary}</p>
                      <div className="tags">
                        {item.tags.map(tag => (
                          <span className="tag" key={`${item.company}-${tag}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section
            id="expertise"
            className="section-block dark-section"
            aria-labelledby="expertise-title">
            <span id="services" aria-hidden="true" />
            <div className="section-inner" data-reveal="">
              <div className="section-heading">
                <p className="kicker">{portfolio.services.kicker}</p>
                <h2 className="section-title" id="expertise-title">
                  {portfolio.services.title}
                </h2>
              </div>
              <div className="service-grid">
                {portfolio.services.items.map(item => (
                  <article className="service-card" key={item.title}>
                    <div className="service-number">{item.number}</div>
                    <h3 className="service-title">{item.title}</h3>
                    <p className="service-text">{item.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="skills" className="section-block soft" aria-labelledby="skills-title">
            <div className="section-inner" data-reveal="">
              <div className="section-heading">
                <p className="kicker">{portfolio.skills.kicker}</p>
                <h2 className="section-title" id="skills-title">
                  {portfolio.skills.title}
                </h2>
              </div>
              <div className="skills-grid">
                {portfolio.skills.groups.map(group => (
                  <div key={group.label}>
                    <h3 className="skill-label">{group.label}</h3>
                    <div className="skill-list">
                      {group.items.map(skill => (
                        <span className="skill-pill" key={`${group.label}-${skill}`}>
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="cv" className="cv-section" aria-labelledby="cv-title">
            <div className="cv-inner" data-reveal="">
              <p className="kicker">{portfolio.cv.kicker}</p>
              <h2 className="section-title" id="cv-title">
                {portfolio.cv.title}
              </h2>
              <p className="cv-body">{portfolio.cv.body}</p>
              <a
                className="dark-pill"
                href={portfolio.cv.file.url}
                download={portfolio.cv.file.downloadName}>
                <svg
                  className="cv-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M12 3v12" />
                  <path d="m7 10 5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>
                {portfolio.cv.cta}
              </a>
            </div>
          </section>

          <section
            id="contact"
            className="section-block dark-section contact-section"
            aria-labelledby="contact-title">
            <div className="contact-watermark">
              <IconLogo />
            </div>
            <div className="contact-inner" data-reveal="">
              <p className="kicker">{portfolio.contact.kicker}</p>
              <h2 className="contact-title" id="contact-title">
                {portfolio.contact.title}
              </h2>
              <p className="contact-body">{portfolio.contact.body}</p>
              <div className="availability">
                <span className="availability-dot" />
                {portfolio.contact.availability}
              </div>
              <div className="contact-actions">
                <a className="light-pill" href={`mailto:${email}`}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-10 5L2 7" />
                  </svg>
                  {portfolio.contact.emailLabel}
                </a>
                {linkedInUrl && (
                  <a className="outline-pill" href={linkedInUrl} target="_blank" rel="noreferrer">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 17V10.5H6.2V17h2.14zM7.27 9.57a1.24 1.24 0 1 0 0-2.48 1.24 1.24 0 0 0 0 2.48zM18 17v-3.57c0-1.9-.4-3.36-2.63-3.36-1.07 0-1.79.59-2.08 1.14h-.03V10.5h-2.05V17h2.14v-3.21c0-.85.16-1.67 1.21-1.67s1.05.96 1.05 1.72V17H18z" />
                    </svg>
                    {portfolio.contact.linkedinLabel}
                  </a>
                )}
              </div>
            </div>
          </section>

          <footer className="site-footer">
            <div className="footer-inner">
              <div className="footer-brand">
                <HMark compact />
                <div>
                  <div className="footer-name">Hedi Hidouri</div>
                  <div className="footer-tagline">{portfolio.footer.tagline}</div>
                </div>
              </div>
              <div className="footer-rights">{portfolio.footer.rights}</div>
            </div>
          </footer>
        </main>

        <WhatsAppFab />
      </PortfolioRoot>
    </>
  );
};

PortfolioPageContent.propTypes = {
  location: PropTypes.object.isRequired,
};

const HomePage = ({ location, language }) => (
  <LanguageProvider initialLanguage={language}>
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <PortfolioPageContent location={location} />
    </ThemeProvider>
  </LanguageProvider>
);

HomePage.propTypes = {
  location: PropTypes.object.isRequired,
  language: PropTypes.oneOf(['en', 'fr']).isRequired,
};

export default HomePage;
